import { useCallback, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { BOX_TRUCK_MAP_MAX_LOAD_STATES, type BoxTruckStateLoadCount } from '@numeo/types-core'
import { fetchBoxTruckMapStateLoads } from '../services/boxTruckMapApi'

const normalizeStateCodes = (stateCodes: string[]): string[] =>
    [...new Set(stateCodes.map((stateCode) => stateCode.trim().toUpperCase()).filter((stateCode) => /^[A-Z]{2}$/.test(stateCode)))].slice(
        0,
        BOX_TRUCK_MAP_MAX_LOAD_STATES
    )

export const useBoxTruckMapStateLoads = () => {
    const [loadsByState, setLoadsByState] = useState<BoxTruckStateLoadCount[]>([])
    const [loadingStateCodes, setLoadingStateCodes] = useState<Set<string>>(new Set())
    const [failedStateCodes, setFailedStateCodes] = useState<Set<string>>(new Set())
    const loadedStateCodes = useRef(new Set<string>())
    const requestVersions = useRef(new Map<string, number>())

    const loadStateCounts = useCallback(async (stateCodes: string[], force = false) => {
        const normalized = normalizeStateCodes(stateCodes)
        const requested = force ? normalized : normalized.filter((stateCode) => !loadedStateCodes.current.has(stateCode))
        if (requested.length === 0) return

        const versions = new Map<string, number>()
        requested.forEach((stateCode) => {
            loadedStateCodes.current.add(stateCode)
            const version = (requestVersions.current.get(stateCode) ?? 0) + 1
            requestVersions.current.set(stateCode, version)
            versions.set(stateCode, version)
        })
        const isCurrentRequest = (stateCode: string): boolean => requestVersions.current.get(stateCode) === versions.get(stateCode)
        setLoadingStateCodes((current) => new Set([...current, ...requested]))
        setFailedStateCodes((current) => {
            const next = new Set(current)
            requested.forEach((stateCode) => next.delete(stateCode))
            return next
        })

        try {
            const returnedCounts = await fetchBoxTruckMapStateLoads(requested)
            const returnedByState = new Map(returnedCounts.map((row) => [row.state, row.count]))
            setLoadsByState((current) => {
                const next = new Map(current.map((row) => [row.state, row.count]))
                requested.filter(isCurrentRequest).forEach((state) => next.set(state, returnedByState.get(state) ?? 0))
                return Array.from(next, ([state, count]) => ({ state, count }))
            })
        } catch (cause) {
            const currentFailures = requested.filter(isCurrentRequest)
            currentFailures.forEach((stateCode) => loadedStateCodes.current.delete(stateCode))
            if (currentFailures.length > 0) {
                setFailedStateCodes((current) => new Set([...current, ...currentFailures]))
                Sentry.captureException(cause, {
                    tags: { feature: 'box-truck-map', operation: 'load-state-counts' },
                    extra: { stateCodes: currentFailures },
                })
            }
        } finally {
            setLoadingStateCodes((current) => {
                const next = new Set(current)
                requested.filter(isCurrentRequest).forEach((stateCode) => next.delete(stateCode))
                return next
            })
        }
    }, [])

    return { loadsByState, loadingStateCodes, failedStateCodes, loadStateCounts }
}
